'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  Building2,
  Calculator,
  AlertTriangle,
  ClipboardList,
  Users,
  TrendingUp,
  Shield,
  MapPin,
  Scale,
  Clock,
  CheckCircle,
  LayoutGrid,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DemoBriefingModal } from '@/components/briefing/demo-briefing-modal';
import {
  DEMO_MA_COMPANY_NAME,
  getDemoMaBriefingMarkdown,
} from '@/lib/demo-ma-briefing';

const features = [
  {
    icon: Building2,
    title: 'Target-Profil mit Deal-Kontext',
    desc: 'Vollständige Firmenstruktur mit UBO-Kette, Tochtergesellschaften, Chronologie aus NorthData, Geschäftsführer-Wechseln und Standorten als Basis für jeden Deal.',
  },
  {
    icon: Calculator,
    title: 'Bewertungsrahmen',
    desc: 'Enterprise Value in drei Szenarien (konservativ, base, optimistisch) mit Peer-Multiples, geschätztem ARR samt Konfidenzangabe, Funding-Verlauf und Analyse von Burn Rate und Runway.',
  },
  {
    icon: AlertTriangle,
    title: 'Red Flag Matrix',
    desc: 'Risiken quantifiziert nach Severity × Wahrscheinlichkeit × Impact. Von lückenhafter Finanztransparenz bis zur Abhängigkeit von einzelnen Vendoren, jeweils mit konkreter DD-Aktion.',
  },
  {
    icon: Users,
    title: 'Key-Man-Bewertung',
    desc: 'Gründer und C-Level mit Werdegang, Verifikationsstatus, Key-Man-Score 0–10 und Übertragbarkeit des Netzwerks. Inklusive historischer Gründungen und bisheriger Exits.',
  },
  {
    icon: TrendingUp,
    title: 'Investment-Hypothesen',
    desc: '5–7 testbare Deal-Hypothesen mit Evidenz für und gegen, Einschätzung und konkreter DD-Anweisung zur Validierung. Sofort verwendbar im Investment-Memo.',
  },
  {
    icon: ClipboardList,
    title: 'Deal-Struktur & Management-Fragen',
    desc: 'Konkrete Empfehlungen zu Share oder Asset Deal, Earn-Out-KPIs, Retention-Paketen und Garantien. Plus 10–12 Management-Fragen mit Antwort-Signalen für Gute und Warnsignale.',
  },
];

const sampleSections = [
  { num: '0', title: 'Identitätsklärung: Target verifiziert, Auftrags-Prämisse korrigiert' },
  { num: '1', title: 'Executive Summary mit Deal Attractiveness Score und Kaufpreisrange' },
  { num: '2', title: 'Unternehmensprofil, Konzerndiagramm, Register-Chronologie' },
  { num: '3', title: 'Key Persons mit Key-Man-Scoring und Netzwerk-Übertragbarkeit' },
  { num: '4', title: 'Finanzanalyse: 5-Jahres-Dashboard und KPI-Warnsignale' },
  { num: '5', title: 'Markt, Wettbewerb und Exit-Optionen' },
  { num: '6', title: 'Kunden, Konzentration und Abhängigkeiten' },
  { num: '7', title: 'Mitarbeiter & Kultur (Hypergrowth-Stresstest)' },
  { num: '8', title: 'Rechtsrisiken & Compliance' },
  { num: '9', title: 'Red Flag Matrix mit Score-Methodik' },
  { num: '10', title: 'Investment-Hypothesen mit Pro- und Contra-Evidenz' },
  { num: '11', title: 'Deal-Struktur-Empfehlung (Share oder Asset, Earn-Out, Retention)' },
  { num: '12', title: 'Management-Fragen mit Antwort-Signalen' },
  { num: '13', title: 'DD-Roadmap in 3 Phasen' },
  { num: '14', title: 'Fazit, Novel Insights und Datenqualitäts-Score' },
  { num: '15', title: 'Quellen-Übersicht' },
  { num: '16', title: 'Disclaimer' },
];

function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/ma-waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'm-a-landing' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.');
        return;
      }
      setSubmitted(true);
      toast.success('Vielen Dank. Wir melden uns, sobald M&A live geht.');
    } catch {
      toast.error('Netzwerkfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="rounded-xl border border-accent/30 bg-accent/5 p-6 text-center">
        <CheckCircle className="mx-auto mb-2 h-8 w-8 text-accent" />
        <p className="text-base font-semibold text-foreground">Sie stehen auf der Liste.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Wir melden uns mit einer Einladung, sobald die M&A-Briefings live gehen.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
      <Input
        type="email"
        required
        placeholder="ihre.adresse@kanzlei.de"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-12 flex-1"
        aria-label="E-Mail-Adresse"
      />
      <Button type="submit" size="lg" variant="accent" disabled={submitting} className="min-h-[44px]">
        {submitting ? 'Wird gespeichert…' : 'Vormerken'}
        {!submitting && <ArrowRight className="ml-1 h-4 w-4" />}
      </Button>
    </form>
  );
}

export function MaLandingClient() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-teal-50 dark:bg-card">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <div className="max-w-xl">
              <Badge variant="outline" className="mb-4 border-amber-500/50 text-amber-700 dark:text-amber-400">
                <Clock className="mr-1 h-3 w-3" /> Bald verfügbar · Early Access
              </Badge>
              <h1 className="text-3xl font-extrabold tracking-tight text-foreground leading-tight md:text-5xl">
                Pre-DD-Intelligenz,{' '}
                <span className="text-gradient">bevor Sie den ersten Anruf machen.</span>
              </h1>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                Sie geben den Firmennamen ein, salesbriefing.de erstellt ein
                Pre-DD-Briefing mit 16 Sektionen plus Identitätsklärung auf Niveau eines Senior-Beraters: Target-Profil,
                Bewertungsrahmen, Red Flag Matrix mit Score-Methodik, Investment-Hypothesen
                mit konkreten DD-Anweisungen und Empfehlungen zur Deal-Struktur.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  variant="hero"
                  size="lg"
                  className="min-h-[44px]"
                  onClick={() => setDemoOpen(true)}
                >
                  Beispiel-Briefing ansehen
                </Button>
              </div>
              <div className="mt-8 max-w-md">
                <p className="mb-3 text-sm font-medium text-foreground">
                  Auf die Warteliste setzen. Wir benachrichtigen Sie zuerst:
                </p>
                <WaitlistForm />
                <p className="mt-2 text-xs text-muted-foreground">
                  Keine Spam-Welle. Eine einzige Mail mit Einladung, sobald M&A live geht.
                </p>
              </div>
            </div>
            <div
              className="relative cursor-pointer"
              onClick={() => setDemoOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDemoOpen(true);
                }
              }}
              aria-label="Komplettes Beispiel-Briefing öffnen"
            >
              <SampleBriefingPreview />
              <div className="absolute inset-0 rounded-xl opacity-0 hover:opacity-100 transition-opacity bg-black/5 dark:bg-white/5 flex items-center justify-center">
                <span className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold shadow-lg">
                  Komplettes Beispiel-Briefing öffnen
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-teal-50 dark:bg-background py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              Was in einem M&A-Briefing steckt
            </h2>
            <p className="mt-3 text-muted-foreground">
              Ein vollständiges Pre-DD-Dossier zur direkten Verwendung im Investment-Memo oder Pitch.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <Card key={f.title} className="card-shadow hover:card-shadow-hover transition-shadow border-border">
                <CardContent className="p-6">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sections overview */}
      <section className="bg-teal-50 dark:bg-card py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              16 Sektionen plus Identitätsklärung, ein Dokument
            </h2>
            <p className="mt-3 text-muted-foreground">
              Die gleiche Logik, die Sie aus M&A-Boutiquen kennen, in jedem Briefing strukturiert wiederverwendbar.
            </p>
          </div>
          <div className="mx-auto grid max-w-4xl gap-3 sm:grid-cols-2">
            {sampleSections.map((s) => (
              <div key={s.num} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 card-shadow">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground text-sm font-bold">
                  {s.num}
                </div>
                <p className="text-sm font-medium text-foreground leading-relaxed">{s.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust + waitlist (bottom) */}
      <section className="bg-teal-50 dark:bg-background py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-8 card-shadow">
            <div className="text-center">
              <Badge variant="outline" className="mb-3 border-amber-500/50 text-amber-700 dark:text-amber-400">
                <Clock className="mr-1 h-3 w-3" /> Coming Soon
              </Badge>
              <h2 className="text-2xl font-bold text-foreground">
                Setzen Sie sich auf die Liste
              </h2>
              <p className="mt-2 text-muted-foreground">
                Wir öffnen den Zugang schrittweise für M&A-Berater, Corporate-Development-Teams
                und Nachfolge-Berater. Die Plätze sind begrenzt.
              </p>
            </div>
            <div className="mt-6 mx-auto max-w-md">
              <WaitlistForm />
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <Shield className="h-4 w-4 text-accent" /> DSGVO-konform
            </div>
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <MapPin className="h-4 w-4 text-accent" /> Hosting in Deutschland
            </div>
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <Scale className="h-4 w-4 text-accent" /> Keine vollständige Due Diligence
            </div>
          </div>

          <p className="mx-auto mt-8 max-w-3xl text-center text-xs text-muted-foreground leading-relaxed">
            M&A-Briefings basieren ausschließlich auf öffentlich zugänglichen Quellen
            (NorthData, Bundesanzeiger, KvK, Handelsregister, Fachpresse, Venture-Datenbanken).
            Sie stellen keine vollständige Due Diligence dar und ersetzen nicht die Prüfung
            nicht-öffentlicher Daten. Alle Schätzungen werden als solche gekennzeichnet.
            Jedes Briefing sollte durch einen M&A-Experten validiert werden.
          </p>
        </div>
      </section>

      {/* Main CTA */}
      <section className="bg-gradient-to-br from-teal-700 via-teal-800 to-teal-900 py-20 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white md:text-5xl tracking-tight leading-tight">
            Wissen, was im Target steckt.
            <br />
            Vor dem ersten Term Sheet.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-white/80 leading-relaxed">
            Sichern Sie sich Early Access. Sobald die ersten M&amp;A-Briefings live gehen,
            sind Sie zuerst dran.
          </p>
          <div className="mx-auto mt-8 max-w-md">
            <WaitlistForm />
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Eine einzige Mail bei Launch
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> DSGVO-konform
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Hosting in Deutschland
            </span>
          </div>
        </div>
      </section>

      {/* Footer-CTA cross-link */}
      <section className="bg-gradient-to-br from-teal-700 via-teal-800 to-teal-900 py-12 border-t border-white/10">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/80">
            Sie suchen die Versicherungs-Variante?{' '}
            <Link href="/versicherung" className="font-semibold text-white underline">
              Zur Versicherungs-Recherche
            </Link>
          </p>
        </div>
      </section>

      {/* Full demo briefing modal */}
      <DemoBriefingModal
        open={demoOpen}
        onOpenChange={setDemoOpen}
        companyName={DEMO_MA_COMPANY_NAME}
        markdown={getDemoMaBriefingMarkdown()}
        badgeLabel="Fiktives Beispiel"
        ctaHref="/register?ref=m-a-demo"
      />
    </div>
  );
}

function SampleBriefingPreview() {
  return (
    <>
      {/* Mobile / Tablet variant (< lg) */}
      <div className="block lg:hidden w-full rounded-xl border border-border bg-card overflow-hidden card-shadow-lg select-none">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3 bg-muted/30 min-w-0">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">
            SB
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              Auriga Cybershield GmbH
            </p>
            <p className="text-xs text-muted-foreground">Erstellt: heute · Fiktives Beispiel</p>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-bold flex-shrink-0">
                1
              </span>
              <span className="text-sm font-semibold text-foreground">Executive Summary</span>
              <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 ml-auto">
                Score 6,3/10
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-2">
              MDR + Cyber-Insurance-Bundle. 100%-Tochter einer DK-Holding; IP und FSA-Lizenz in Kopenhagen.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-xs text-muted-foreground">ARR (est.)</p>
                <p className="text-xs font-medium text-foreground">$10–13M</p>
              </div>
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-xs text-muted-foreground">EV-Korridor</p>
                <p className="text-xs font-medium text-foreground">$60–130M</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-accent/30 bg-accent/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-bold flex-shrink-0">
                9
              </span>
              <span className="text-sm font-semibold text-foreground">Red Flag Matrix</span>
              <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 ml-auto">
                15 Punkte
              </Badge>
            </div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li className="flex items-center gap-1.5">
                <span className="text-red-600 dark:text-red-400 flex-shrink-0">🔴</span>
                <span>Gründer-Key-Man-Risiko: 25/25</span>
              </li>
              <li className="flex items-center gap-1.5">
                <span className="text-amber-600 dark:text-amber-400 flex-shrink-0">🟠</span>
                <span>Risikoträger-Abhängigkeit: 20/25</span>
              </li>
              <li className="flex items-center gap-1.5">
                <span className="text-amber-600 dark:text-amber-400 flex-shrink-0">🟠</span>
                <span>Finanztransparenz 2024/2025: 20/25</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="h-8 bg-gradient-to-t from-card to-transparent -mt-4 relative z-10" />
      </div>

      {/* Desktop variant (lg+) */}
      <div className="hidden lg:block w-full rounded-xl border border-border bg-card overflow-hidden card-shadow-lg select-none">
        <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-muted/30">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">
              SB
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                Auriga Cybershield GmbH
              </p>
              <p className="text-[10px] text-muted-foreground">Erstellt: heute · Fiktives Beispiel</p>
            </div>
          </div>
          <div className="flex items-center rounded-md border border-border p-0.5 flex-shrink-0">
            <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-accent text-accent-foreground">
              <LayoutGrid className="h-3 w-3" />
            </div>
            <div className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground">
              <FileText className="h-3 w-3" />
            </div>
          </div>
        </div>

        {/* Mini tab row */}
        <div className="flex gap-1 px-4 py-2 border-b border-border overflow-hidden">
          {['Executive Summary', 'Bewertung', 'Red Flag Matrix', 'Hypothesen', 'Deal-Struktur'].map(
            (tab, i) => (
              <span
                key={tab}
                className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${
                  i === 2
                    ? 'bg-accent/15 text-accent font-medium'
                    : 'text-muted-foreground'
                }`}
              >
                {tab}
              </span>
            ),
          )}
        </div>

        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent text-accent-foreground text-[8px] font-bold">
                1
              </span>
              <span className="text-xs font-semibold text-foreground">Executive Summary</span>
              <Badge variant="secondary" className="text-[8px] px-1.5 py-0 h-4 ml-auto">
                Deal Score 6,3/10
              </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
              MDR + Cyber-Insurance-Bundle, 100%-Tochter einer DK-Holding. IP, SOC und FSA-Lizenz liegen in Kopenhagen; die GmbH ist Vertriebsvehikel.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-[9px] text-muted-foreground">ARR (est.)</p>
                <p className="text-[11px] font-medium text-foreground">$10–13M</p>
              </div>
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-[9px] text-muted-foreground">EV-Korridor</p>
                <p className="text-[11px] font-medium text-foreground">$60–130M</p>
              </div>
              <div className="rounded-md bg-muted/50 px-2 py-1.5">
                <p className="text-[9px] text-muted-foreground">Mitarbeiter</p>
                <p className="text-[11px] font-medium text-foreground">~230</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-accent/30 bg-accent/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent text-accent-foreground text-[8px] font-bold">
                9
              </span>
              <span className="text-xs font-semibold text-foreground">Red Flag Matrix</span>
              <Badge variant="secondary" className="text-[8px] px-1.5 py-0 h-4 ml-auto">
                15 Punkte
              </Badge>
            </div>
            <div className="space-y-1">
              <div className="grid grid-cols-12 gap-1 text-[9px] font-medium text-muted-foreground pb-1 border-b border-border/50">
                <span className="col-span-7">Red Flag</span>
                <span className="col-span-2 text-right">Score</span>
                <span className="col-span-3 text-right">Severity</span>
              </div>
              {[
                { flag: 'Gründer-Key-Man-Risiko', score: '25/25', sev: '🔴 KRITISCH', sevColor: 'text-red-600 dark:text-red-400' },
                { flag: 'Risikoträger-Abhängigkeit', score: '20/25', sev: '🟠 HOCH', sevColor: 'text-amber-600 dark:text-amber-400' },
                { flag: 'Finanztransparenz 2024/25', score: '20/25', sev: '🟠 HOCH', sevColor: 'text-amber-600 dark:text-amber-400' },
                { flag: 'Cyber-MGA-Vorgeschichte CIO/CUO', score: '16/25', sev: '🟠 HOCH', sevColor: 'text-amber-600 dark:text-amber-400' },
              ].map((row) => (
                <div key={row.flag} className="grid grid-cols-12 gap-1 text-[10px]">
                  <span className="col-span-7 text-foreground truncate">{row.flag}</span>
                  <span className="col-span-2 text-right font-mono text-muted-foreground">{row.score}</span>
                  <span className={`col-span-3 text-right ${row.sevColor}`}>{row.sev}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="h-8 bg-gradient-to-t from-card to-transparent -mt-4 relative z-10" />
      </div>
    </>
  );
}
